import * as React from "react";
import { format } from "date-fns";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { parseLocalISODate } from "@/lib/dateUtils";

export interface DatePickerProps {
  /** Lands on the native input, so a `<Label htmlFor>` can point at it. */
  id?: string;
  value?: Date;
  onChange: (date: Date | undefined) => void;
  includeTime?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  min?: Date;
  max?: Date;
  /** Forwarded to the native input, for an inline field error. */
  "aria-invalid"?: boolean | "true" | "false";
  /** Forwarded to the native input; point it at the inline error's id. */
  "aria-describedby"?: string;
}

export function DatePicker({
  id,
  value,
  onChange,
  includeTime = false,
  disabled = false,
  placeholder,
  className,
  min,
  max,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: DatePickerProps) {
  const inputType = includeTime ? "datetime-local" : "date";
  const formatString = includeTime ? "yyyy-MM-dd'T'HH:mm" : "yyyy-MM-dd";

  const formatted = value ? format(value, formatString) : "";

  // Native date/datetime inputs are segmented (e.g. MM/DD/YYYY). Feeding a
  // re-formatted `value` back on every keystroke resets the segment being
  // typed — so typing a 4-digit year is impossible (each digit just replaces
  // the rightmost one). We mirror the raw input text locally and only re-sync
  // from props when the field is NOT focused, so typing is never interrupted
  // while the parsed value still flows up through onChange.
  const [text, setText] = React.useState(formatted);
  const focusedRef = React.useRef(false);

  React.useEffect(() => {
    if (!focusedRef.current) setText(formatted);
  }, [formatted]);

  return (
    <Input
      id={id}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      type={inputType}
      disabled={disabled}
      className={cn("w-full", className)}
      placeholder={placeholder}
      value={text}
      min={min ? format(min, formatString) : undefined}
      max={max ? format(max, formatString) : undefined}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={() => {
        focusedRef.current = false;
        // Re-sync to the canonical formatted value once editing ends.
        setText(formatted);
      }}
      onChange={(e) => {
        // Echo exactly what the input reported so the browser's segment
        // buffer is never reset mid-type.
        setText(e.target.value);

        if (!e.target.value) {
          onChange(undefined);
          return;
        }

        if (includeTime) {
          // For datetime, use Date constructor (value is already in local time format)
          onChange(new Date(e.target.value));
        } else {
          // For date-only, use parseLocalISODate to prevent timezone shifts
          onChange(parseLocalISODate(e.target.value));
        }
      }}
    />
  );
}
